import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  getPhoneInputMask,
  correctShowingTypeLabel,
  featureEnabled,
} from 'utils';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { message, Required } from 'components';

import CustomContactButtons from './CustomContactButtons';

const commonFieldKeys = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'companyName',
  'companyPhone',
  'companyAddress',
];

const trimValues = val => {
  const newVal = { ...val };
  commonFieldKeys.forEach(key => {
    if (newVal[key]) {
      newVal[key] = newVal[key].trim();
    }
  });
  return newVal;
};

const Step1CustomContactForm = ({
  appointmentTypes,
  bookFor,
  customContact,
  customContactSaved,
  naTypeMap,
  nextStepLabel,
  nonAgentTypes,
  nonAgent = {},
  // functions
  updateState,
  customContactSave,
  customContactEdit,
  deleteCustomAgentOrContact,
  selectSearchRoster,
}) => {
  const phoneRef = useRef();
  const companyPhoneRef = useRef();
  const agentFields = {
    firstName: customContact.firstName || '',
    lastName: customContact.lastName || '',
    email: customContact.email || '',
    phone: customContact.phone || '',
    companyName: (customContact.org && customContact.org.name) || '',
    companyPhone: (customContact.org && customContact.org.phone) || '',
    companyAddress: (customContact.org && customContact.org.address) || '',
    note: '',
    showingType: customContact.showingType || appointmentTypes[0],
  };
  const nonAgentFields = {
    firstName: nonAgent.firstName || '',
    lastName: nonAgent.lastName || '',
    email: nonAgent.email || '',
    phone: nonAgent.phone || '',
    companyName: nonAgent.companyName || '',
    companyPhone: nonAgent.companyPhone || '',
    companyAddress: nonAgent.companyAddress || '',
    naType: nonAgent.type || nonAgentTypes[0],
  };

  const validate = preValues => {
    const values = trimValues(preValues);
    const errors = {};
    let errorCount = 0;
    if (!values.firstName) {
      errors.firstName = 'First name is required.';
      errorCount += 1;
    }
    if (!values.lastName) {
      errors.lastName = 'Last name required.';
      errorCount += 1;
    }
    if (values.phone && !getPhoneInputMask().isValid(values.phone)) {
      errors.phone = 'Invalid phone number';
      errorCount += 1;
    }
    if (bookFor === 'nonAgent' && !values.email) {
      errors.email = 'Email is required.';
      errorCount += 1;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address.';
      errorCount += 1;
    }
    if (!values.companyName) {
      errors.companyName = 'Company name is required.';
      errorCount += 1;
    }
    if (!values.companyPhone) {
      errors.companyPhone = 'Company phone is required.';
      errorCount += 1;
    } else if (!getPhoneInputMask().isValid(values.companyPhone)) {
      errors.companyPhone = 'Invalid phone number';
      errorCount += 1;
    }
    if (errorCount)
      message.warning(
        `At least ${errorCount} field${
          errorCount !== 1 ? 's need to be revised' : ' needs to be revised'
        }`,
      );

    return errors;
  };

  useEffect(() => {
    const input = phoneRef.current;
    const input2 = companyPhoneRef.current;
    getPhoneInputMask().mask([input, input2]);
  }, []);

  return (
    <div>
      <Formik
        initialValues={bookFor === 'nonAgent' ? nonAgentFields : agentFields}
        validate={values => validate(values)}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(preValues, { setSubmitting }) => {
          const values = trimValues(preValues);
          updateState(draft => {
            const {
              companyName: name,
              companyPhone: phone,
              companyAddress: address,
              note,
              ...contact
            } = values;
            draft.customContact = { ...contact };
            draft.customContact.org = { name, phone, address };
            draft.appointment.type = values.naType || values.showingType;
            draft.appointment.note = note;
          });
          customContactSave(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="row">
              {bookFor === 'nonAgent' && (
                <div className="form-group col-md-12">
                  <label>Person is a:</label>
                  <Field
                    component="select"
                    name="naType"
                    className="form-control"
                    data-cy="person-type"
                  >
                    {nonAgentTypes.map(at => (
                      <option key={at} value={at}>
                        {naTypeMap[at]}
                      </option>
                    ))}
                  </Field>
                </div>
              )}
              <div className="form-group col-md-12">
                <span>
                  {' '}
                  <Required /> = Required field
                </span>
              </div>
              <div className="form-group col-md-6">
                <label>
                  First Name <Required />
                </label>
                <Field
                  type="text"
                  className="form-control"
                  name="firstName"
                  placeholder="First Name"
                  data-cy="custom-first-name"
                />
                <ErrorMessage
                  name="firstName"
                  component="label"
                  className="error"
                />
              </div>
              <div className="form-group col-md-6">
                <label>
                  Last Name <Required />
                </label>
                <Field
                  type="text"
                  className="form-control"
                  name="lastName"
                  placeholder="Last Name"
                  data-cy="custom-last-name"
                />
                <ErrorMessage
                  name="lastName"
                  component="label"
                  className="error"
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <label>
                  Email <Required />
                </label>
                <Field
                  type="text"
                  className="form-control"
                  name="email"
                  placeholder="Email"
                  data-cy="custom-email"
                />
                <ErrorMessage
                  name="email"
                  component="label"
                  className="error"
                />
              </div>
              <div className="form-group col-md-6">
                <label>Mobile</label>
                <Field
                  type="text"
                  className="form-control"
                  name="phone"
                  placeholder="Phone"
                  data-cy="custom-phone"
                  innerRef={phoneRef}
                />
                <ErrorMessage
                  name="phone"
                  component="label"
                  className="error"
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <label>
                  Company Name <Required />
                </label>
                <Field
                  type="text"
                  className="form-control"
                  name="companyName"
                  placeholder="Company Name"
                  data-cy="custom-company-name"
                />
                <ErrorMessage
                  name="companyName"
                  component="label"
                  className="error"
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group col-md-6">
                <label>
                  Company Phone <Required />
                </label>
                <Field
                  type="text"
                  className="form-control"
                  name="companyPhone"
                  placeholder="Company Phone"
                  data-cy="custom-company-phone"
                  innerRef={companyPhoneRef}
                />
                <ErrorMessage
                  name="companyPhone"
                  component="label"
                  className="error"
                />
              </div>

              <div className="form-group col-md-6">
                <label>Company Address</label>
                <Field
                  type="text"
                  className="form-control"
                  name="companyAddress"
                  placeholder="Company Address"
                  data-cy="custom-company-address"
                />
              </div>
            </div>
            {featureEnabled('nonAgentNotificationFeature') &&
              bookFor === 'nonAgent' && (
                <StyledNotice>
                  Listing agent to confirm approval for this contact
                </StyledNotice>
              )}
            {featureEnabled('nonAgentNotificationFeature') &&
              bookFor !== 'nonAgent' && (
                <div className="important m-b-sm m-t-sm">
                  This person is NOT a verified agent
                </div>
              )}
            {!featureEnabled('nonAgentNotificationFeature') && (
              <div className="form-group">
                <label>Send instructions by calling office.</label>
              </div>
            )}

            {bookFor !== 'nonAgent' && (
              <div className="form-group">
                <label>Showing Type</label>
                <Field
                  name="showingType"
                  component="select"
                  className="form-control"
                  data-cy="showing-type"
                >
                  {appointmentTypes &&
                    appointmentTypes.map(at => (
                      <option key={at} value={at}>
                        {correctShowingTypeLabel(at)}
                      </option>
                    ))}
                </Field>
              </div>
            )}

            {/* CustomContact bottom buttons  */}
            {customContact && (
              <CustomContactButtons
                customContactSaved={customContactSaved}
                deleteCustomAgentOrContact={deleteCustomAgentOrContact}
                selectSearchRoster={selectSearchRoster}
                bookFor={bookFor}
                nextStepLabel={nextStepLabel}
                customContactEdit={customContactEdit}
                isSubmitting={isSubmitting}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

const StyledNotice = styled.p`
  margin-bottom: 16px;
`;

export default Step1CustomContactForm;
